/* eslint-disable no-console */
import {
  Accordion,
  ActionIcon,
  Alert,
  Anchor,
  Badge,
  Button,
  Card,
  Checkbox,
  Divider,
  Flex,
  Group,
  Loader,
  Modal,
  Pagination,
  ScrollArea,
  Select,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  Textarea
} from '@mantine/core';
import { DragDropContext, Draggable, Droppable, DropResult } from '@hello-pangea/dnd';
import {
  IconAlertTriangle,
  IconEdit,
  IconGripVertical,
  IconPlus,
  IconSearch,
  IconTrash
} from '@tabler/icons-react';
import { useDisclosure } from '@mantine/hooks';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import DataTables from 'components/DataTables';
import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { createSearchParams, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { reviewMasterPlanService } from 'services/dacum/reviewMasterPlan.service';
import { staticCompetencyData } from 'modules/pages/dacum/competencyManagement/TableCompetencyManagement';
import { masterPlanService } from 'services/dacum/masterPlan.service';
import { courseService } from 'services/lcms/course.service';
import { coreCompetencyService } from 'services/dacum/coreCompetency.service';
import { CurriculumMappingRecord } from 'types/dacum/curriculumMapping';
import {
  CreateMasterPlanPayload,
  MasterPlanCoursePayload,
  MasterPlanDetailItem,
  UpdateMasterPlanPayload
} from 'types/dacum/masterPlan';
import { showErrorNotification, showSuccessNotification } from 'utils/notification';
import { pathnames } from 'routes/dacum';

interface SelectedCourse {
  courseId: string;
  courseName: string;
  courseSequence: number;
  courseCode?: string;
  courseCategory?: string;
  status?: string;
  competencyCategory?: string;
  scheduleStartDate?: string | null;
  scheduleEndDate?: string | null;
  createdAt?: string;
  createdBy?: string;
  checkInByName?: string;
  checkInDateTime?: string | null;
  checkOutByName?: string;
  checkOutDateTime?: string | null;
  isRequired: number;
}

interface CreateMasterPlanFormValues {
  programName: string;
  description: string;
  keyAction: string;
  tahun: string;
  endYear: string;
  note?: string;
}

interface ExtendedCurriculumRecord extends CurriculumMappingRecord {
  courseDetails?: {
    learningObjectives?: string;
    content?: string;
    learningHours?: number;
    modeOfDelivery?: string;
    lim?: string;
    c?: string;
    liaIndicator?: boolean;
  };
}

interface CreateMasterPlanFormProps {
  mode?: 'create' | 'edit';
  initialData?: MasterPlanDetailItem | null;
}

interface CreateMasterPlanLocationState {
  jobFamilyId?: string;
  jobFamilyName?: string;
  managementLevel?: string;
  businessUnitId?: string;
  businessUnitName?: string;
  jobProfileId?: string;
  curriculumData?: CurriculumMappingRecord[];
  msSchoolCompetencyManagementID?: string;
}

const DEFAULT_PAGE_SIZE = 10;

type CourseOption = {
  msLCMSCourseID: string;
  courseName: string;
  courseCode?: string;
  courseCategory?: string;
  status?: string;
  competencyCategory?: string;
  scheduleStartDate?: string | null;
  scheduleEndDate?: string | null;
  createdAt?: string;
  createdBy?: string;
  checkInByName?: string;
  checkInDateTime?: string | null;
  checkOutByName?: string;
  checkOutDateTime?: string | null;
};

type CourseSelectionPayload = {
  courseId: string;
  courseName: string;
  courseCode?: string;
  courseCategory?: string;
  status?: string;
  competencyCategory?: string;
  scheduleStartDate?: string | null;
  scheduleEndDate?: string | null;
  createdAt?: string;
  createdBy?: string;
  checkInByName?: string;
  checkInDateTime?: string | null;
  checkOutByName?: string;
  checkOutDateTime?: string | null;
};

const formatDateTime = (value?: string | null) => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '-';
  }
  return parsed.toLocaleString();
};

const formatScheduleRange = (start?: string | null, end?: string | null) => {
  const startLabel = formatDateTime(start);
  const endLabel = formatDateTime(end);
  if (startLabel === '-' && endLabel === '-') {
    return '-';
  }
  if (startLabel !== '-' && endLabel !== '-') {
    return `${startLabel} → ${endLabel}`;
  }
  return startLabel !== '-' ? startLabel : endLabel;
};

const formatCheckInfo = (name?: string, date?: string | null) => {
  if (!name && !date) return '-';
  const dateLabel = formatDateTime(date);
  if (!name) return dateLabel;
  if (dateLabel === '-') return name;
  return `${name} • ${dateLabel}`;
};

const CreateMasterPlanForm: React.FC<CreateMasterPlanFormProps> = ({
  mode = 'create',
  initialData = null
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const state = (location.state || {}) as CreateMasterPlanLocationState;

  const isEditMode = mode === 'edit';
  const currentProgramId = initialData?.msDACUMProgramID || '';

  // Get jobFamily, jobFamilyGroup, year, and endYear from query params (for edit mode navigation back)
  const queryJobFamily = searchParams.get('jobfamily') || '';
  const queryJobFamilyGroup = searchParams.get('jobFamilyGroup') || '';
  const queryYear = searchParams.get('year') || '';
  const queryEndYear = searchParams.get('endYear') || '';

  const jobFamilyIdFromState = state.jobFamilyId || searchParams.get('jobfamilyid') || '';
  const jobFamilyNameFromState = state.jobFamilyName || searchParams.get('jobfamilyname') || '';
  const managementLevelFromState =
    state.managementLevel || searchParams.get('managementlevel') || '';
  const businessUnitIdFromState = state.businessUnitId || searchParams.get('businessunitid') || '';
  const businessUnitNameFromState =
    state.businessUnitName || searchParams.get('businessunitname') || '';
  const jobProfileIdFromState = state.jobProfileId || searchParams.get('jobprofileid') || '';
  const competencyTypeFromState = searchParams.get('competencytype') || 'Technical Competency';
  const jobFamilyGroupNameFromState = searchParams.get('jobfamilygroupname') || '';
  const msSchoolCompetencyManagementIDFromState =
    state.msSchoolCompetencyManagementID ||
    searchParams.get('msSchoolCompetencyManagementID') ||
    '';

  // Determine competency type: use from initialData if in edit mode, otherwise from state/query params
  const competencyTypeFromData = React.useMemo(() => {
    if (isEditMode && initialData?.curriculumList && initialData.curriculumList.length > 0) {
      // Get competency type from the first curriculum item
      return initialData.curriculumList[0].competencyType || 'Technical Competency';
    }
    return competencyTypeFromState;
  }, [isEditMode, initialData, competencyTypeFromState]);

  const jobFamilyId = isEditMode ? '' : jobFamilyIdFromState;
  const jobFamilyName = initialData?.jobFamily || jobFamilyNameFromState;
  const managementLevel = initialData?.jobBand || managementLevelFromState;
  const businessUnitId = initialData?.businessUnitId || businessUnitIdFromState;
  const businessUnitName = businessUnitNameFromState;
  const jobProfileId = isEditMode ? '' : jobProfileIdFromState;
  const competencyType = competencyTypeFromData;
  const jobFamilyGroupName = jobFamilyGroupNameFromState;
  const msSchoolCompetencyManagementID =
    initialData?.msSchoolCompetencyManagementID || msSchoolCompetencyManagementIDFromState;

  const mapCurriculumRecordFromDetail = React.useCallback(
    async (
      item: MasterPlanDetailItem['curriculumList'][number]
    ): Promise<ExtendedCurriculumRecord> => {
      // Handle job positions - check if it's an array of objects with id and name
      const jobPositions = item.jobPositions || [];
      let jobPositionId = '';
      let jobPositionName = '-';

      if (Array.isArray(jobPositions) && jobPositions.length > 0) {
        const firstPos = jobPositions[0] as any;
        jobPositionId =
          typeof firstPos === 'object' && firstPos?.id ? firstPos.id : (firstPos as string);
        jobPositionName = jobPositions
          .map((pos: any) => (typeof pos === 'object' && pos?.name ? pos.name : pos))
          .join(', ');
      }

      // Extract course info if available
      let courseId = null;
      let courseName = null;
      let courseDetails = undefined;

      if (item.courses && Array.isArray(item.courses) && item.courses.length > 0) {
        courseId = item.courses[0].courseId;
        courseName = item.courses[0].courseName;

        // Check if course already has complete details from the API response
        const existingCourse = item.courses[0];
        const hasCompleteData = existingCourse.learningObjectives && existingCourse.modeOfDelivery;

        if (hasCompleteData) {
          // Use existing data if complete
          courseDetails = {
            learningObjectives: existingCourse.learningObjectives || undefined,
            content: undefined, // content is not available in API response
            learningHours: existingCourse.learningHours
              ? parseInt(existingCourse.learningHours)
              : undefined,
            modeOfDelivery: existingCourse.modeOfDelivery || undefined,
            lim: existingCourse.flaggingLIM?.toString(),
            c: existingCourse.C || undefined,
            liaIndicator: existingCourse.flaggingLIA || false
          };
        } else if (courseId) {
          // Fetch course details only if data is incomplete
          try {
            const response = await courseService.detail(courseId);
            const detailData = response.data?.data;
            const basicInfo = detailData?.basicInformation;
            const deliveryDetails = detailData?.deliveryDetails;
            const indicatorStandards = detailData?.indicatorStandards;

            courseDetails = {
              learningObjectives: deliveryDetails?.learningObjectives,
              content: basicInfo?.learningContent,
              learningHours: deliveryDetails?.learningHours,
              modeOfDelivery: basicInfo?.modeOfDelivery,
              lim: indicatorStandards?.limIndicator?.toString(),
              c: indicatorStandards?.c,
              liaIndicator: indicatorStandards?.liaIndicator || false
            };
          } catch (error) {
            console.error('Failed to fetch course details for curriculum:', error);
          }
        }
      }

      return {
        competencyId: item.msCompetencyManagementID,
        businessUnitId: item.businessUnitId,
        businessUnitName: item.businessUnitName || businessUnitName || businessUnitId || '',
        jobPositionId: jobPositionId,
        jobPositionName: jobPositionName,
        jobProfileId: undefined,
        jobBand: item.managementLevel,
        competencyName: item.competencyName,
        competencyDescription: '',
        category: item.category,
        level: item.level.toString(),
        description: '',
        idCourse: courseId,
        courseName: courseName,
        courses: (item.courses || []).map((c: any) => ({
          courseId: c.courseId,
          courseName: c.courseName,
          learningObjectives: c.learningObjectives || '',
          learningHours: c.learningHours ? parseInt(c.learningHours) : 0,
          modeOfDelivery: c.modeOfDelivery || '',
          LIM: c.flaggingLIM || false,
          C: c.C || '',
          LIA: c.flaggingLIA || false
        })),
        jobPositions: item.jobPositions || undefined,
        courseDetails: courseDetails
      };
    },
    [businessUnitId, businessUnitName]
  );

  const [curriculumFromInitialData, setCurriculumFromInitialData] = React.useState<
    ExtendedCurriculumRecord[] | undefined
  >(undefined);

  React.useEffect(() => {
    if (isEditMode && initialData && initialData.curriculumList.length > 0) {
      const loadCurriculumDetails = async () => {
        const mapped = await Promise.all(
          initialData.curriculumList.map(mapCurriculumRecordFromDetail)
        );
        setCurriculumFromInitialData(mapped);
      };
      loadCurriculumDetails();
    }
  }, [initialData, isEditMode, mapCurriculumRecordFromDetail]);

  const curriculumFromState = curriculumFromInitialData || state.curriculumData;

  const [courseModalOpened, { open: openCourseModal, close: closeCourseModal }] =
    useDisclosure(false);
  const [curriculumModalOpened, { open: openCurriculumModal, close: closeCurriculumModal }] =
    useDisclosure(false);
  const [
    curriculumCourseModalOpened,
    { open: openCurriculumCourseModal, close: closeCurriculumCourseModal }
  ] = useDisclosure(false);
  const [selectedCourses, setSelectedCourses] = React.useState<SelectedCourse[]>([]);
  const [editingCurriculumIndex, setEditingCurriculumIndex] = React.useState<number | null>(null);
  const [isPrefilling, setIsPrefilling] = React.useState(false);

  const {
    handleSubmit,
    formState: { errors, isSubmitting },
    register,
    reset,
    control,
    watch,
    setValue
  } = useForm<CreateMasterPlanFormValues>({
    defaultValues: {
      programName: initialData?.programName || '',
      description: initialData?.description || '',
      keyAction: '',
      tahun: initialData?.year?.toString() || new Date().getFullYear().toString(),
      endYear: initialData?.endYear?.toString() || new Date().getFullYear().toString(),
      note: ''
    }
  });

  const selectedYear = watch('tahun');

  React.useEffect(() => {
    if (isEditMode && initialData) {
      reset({
        programName: initialData.programName,
        description: initialData.description,
        keyAction: '', // Hidden field, always empty string
        tahun: initialData.year?.toString() || new Date().getFullYear().toString(),
        endYear: initialData.endYear?.toString() || new Date().getFullYear().toString(),
        note: '' // Always start empty for versioning
      });
    }
  }, [initialData, isEditMode, reset]);

  // Reset endYear when year changes to ensure it's always valid
  React.useEffect(() => {
    if (selectedYear) {
      const startYear = parseInt(selectedYear);
      const currentEndYear = watch('endYear');
      if (currentEndYear) {
        const endYearNum = parseInt(currentEndYear);
        // If endYear is less than startYear, reset it to startYear
        if (endYearNum < startYear) {
          setValue('endYear', startYear.toString());
        }
      } else {
        // If no endYear selected, set it to startYear
        setValue('endYear', startYear.toString());
      }
    }
  }, [selectedYear, setValue, watch]);

  React.useEffect(() => {
    if (!isEditMode && jobFamilyName && managementLevel && (businessUnitName || businessUnitId)) {
      const currentProgramName = watch('programName');
      if (!currentProgramName || currentProgramName.trim() === '') {
        const prefix = `${jobFamilyName} ${businessUnitName || businessUnitId} ${managementLevel} Curriculum`;
        setValue('programName', prefix);
      }
    }
  }, [
    isEditMode,
    jobFamilyName,
    businessUnitName,
    businessUnitId,
    managementLevel,
    watch,
    setValue
  ]);

  const isFilterReady = Boolean(
    (jobFamilyId || jobFamilyName) && managementLevel && (businessUnitId || businessUnitName)
  );

  const { data: competencyResponse, isLoading: loadingCurriculum } = useQuery({
    queryKey: [
      'competencyManagement-for-masterplan',
      jobFamilyId,
      jobFamilyName,
      managementLevel,
      businessUnitId,
      businessUnitName,
      competencyType
    ],
    queryFn: async () => {
      const jobFamilyParam =
        competencyType === 'Technical Competency' ? jobFamilyName || '' : jobFamilyId || '';
      const businessUnitParam =
        competencyType === 'Technical Competency' ? businessUnitName || '' : businessUnitId || '';

      return coreCompetencyService.list({
        pageNumber: 1,
        pageSize: 1000,
        competencyType: competencyType,
        jobFamilyId: jobFamilyParam,
        managementLevel: managementLevel || '',
        businessUnitId: businessUnitParam
      });
    },
    enabled: isFilterReady && !curriculumFromState
  });

  // Transform API response to CurriculumMappingRecord format - same as DetailCompetency page
  const curriculumList: CurriculumMappingRecord[] = React.useMemo(() => {
    if (curriculumFromState && curriculumFromState.length > 0) {
      return curriculumFromState;
    }

    if (!competencyResponse?.data) return [];

    return competencyResponse.data.map((item: any) => ({
      competencyId: item.msCompetencyManagementID,
      businessUnitId: item.businessUnitId,
      businessUnitName: item.businessUnitName,
      jobPositionId: item.jobPosition || '',
      jobPositionName: item.jobPosition || '-',
      jobPositions: item.jobPositions, // Keep the array format
      jobProfileId: undefined,
      jobBand: item.managementLevel,
      competencyName: item.competencyName,
      competencyDescription: item.categoryDescription || '',
      category: item.category,
      level: item.level.toString(),
      levelDescription: item.levelDescription || '',
      description: '',
      // Include course info if available
      idCourse: item.courses && item.courses.length > 0 ? item.courses[0].courseId : null,
      courseName: item.courses && item.courses.length > 0 ? item.courses[0].courseName : null,
      courses: (item.courses || []).map((c: any) => ({
        courseId: c.courseId,
        courseName: c.courseName,
        learningObjectives: c.learningObjectives || '',
        learningHours: c.learningHours ? parseInt(c.learningHours) : 0,
        modeOfDelivery: c.modeOfDelivery || '',
        LIM: c.flaggingLIM || false,
        C: c.C || '',
        LIA: c.flaggingLIA || false
      })),
      year: item.year
    })) as CurriculumMappingRecord[];
  }, [curriculumFromState, competencyResponse]);

  const [curriculumSelection, setCurriculumSelection] = React.useState<ExtendedCurriculumRecord[]>(
    []
  );
  const [initialCurriculum, setInitialCurriculum] = React.useState<ExtendedCurriculumRecord[]>([]);
  const [lastGeneratedNote, setLastGeneratedNote] = React.useState<string>('');

  React.useEffect(() => {
    setCurriculumSelection(curriculumList);
    // Store initial curriculum state for comparison (only once)
    if (initialCurriculum.length === 0 && curriculumList.length > 0) {
      setInitialCurriculum(curriculumList);
    }
  }, [curriculumList, initialCurriculum.length]);

  const buildCurriculumKey = React.useCallback(
    (record: CurriculumMappingRecord) =>
      `${record.jobPositionId}-${record.competencyId}-${record.category}-${record.level}`,
    []
  );

  // Auto-generate note based on curriculum changes
  React.useEffect(() => {
    if (!isEditMode || initialCurriculum.length === 0) return;

    const initialKeys = new Set(initialCurriculum.map(buildCurriculumKey));
    const currentKeys = new Set(curriculumSelection.map(buildCurriculumKey));

    const added = curriculumSelection.filter((item) => !initialKeys.has(buildCurriculumKey(item)));
    const removed = initialCurriculum.filter((item) => !currentKeys.has(buildCurriculumKey(item)));

    if (added.length === 0 && removed.length === 0) {
      // No changes - clear the note if it matches the last generated one
      const currentNote = watch('note');
      if (currentNote === lastGeneratedNote) {
        setValue('note', '');
        setLastGeneratedNote('');
      }
      return;
    }

    const noteLines: string[] = [];

    if (added.length > 0) {
      noteLines.push('Added:');
      added.forEach((item) => {
        noteLines.push(`- ${item.competencyName}`);
      });
    }

    if (removed.length > 0) {
      if (noteLines.length > 0) noteLines.push(''); // Empty line between sections
      noteLines.push('Removed:');
      removed.forEach((item) => {
        noteLines.push(`- ${item.competencyName}`);
      });
    }

    const generatedNote = noteLines.join('\n');
    const currentNote = watch('note');

    // Update if: empty, matches last generated note (user hasn't edited), or user explicitly cleared it
    if (!currentNote || currentNote.trim() === '' || currentNote === lastGeneratedNote) {
      setValue('note', generatedNote);
      setLastGeneratedNote(generatedNote);
    }
  }, [
    curriculumSelection,
    initialCurriculum,
    buildCurriculumKey,
    isEditMode,
    setValue,
    watch,
    lastGeneratedNote
  ]);

  const handleRemoveCurriculum = React.useCallback(
    (record: CurriculumMappingRecord) => {
      if (
        !window.confirm(
          'Remove this curriculum entry from the master plan list? You can restore it anytime before submitting.'
        )
      ) {
        return;
      }
      setCurriculumSelection((prev) =>
        prev.filter((item) => buildCurriculumKey(item) !== buildCurriculumKey(record))
      );
    },
    [buildCurriculumKey]
  );

  const handleEditCurriculumCourse = React.useCallback(
    (index: number) => {
      console.log('handleEditCurriculumCourse called with index:', index);
      console.log('typeof index:', typeof index);
      console.log('Current curriculumSelection length:', curriculumSelection.length);
      setEditingCurriculumIndex(index);
      openCurriculumCourseModal();
    },
    [openCurriculumCourseModal, curriculumSelection.length]
  );

  const handleUpdateCurriculumCourse = React.useCallback(
    async (courseId: string) => {
      if (editingCurriculumIndex === null) {
        console.log('No editing index set');
        return;
      }

      console.log('Updating curriculum at index:', editingCurriculumIndex);
      console.log('New course ID:', courseId);

      try {
        // Fetch course details
        const response = await courseService.detail(courseId);
        const detailData = response.data?.data;
        const basicInfo = detailData?.basicInformation;
        const deliveryDetails = detailData?.deliveryDetails;
        const indicatorStandards = detailData?.indicatorStandards;

        console.log('Course detail response:', detailData);
        console.log('Basic info:', basicInfo);
        console.log('Delivery details:', deliveryDetails);
        console.log('Indicator standards:', indicatorStandards);

        // Store the index value before state update
        const indexToUpdate = editingCurriculumIndex;

        setCurriculumSelection((prev) => {
          console.log('Previous curriculum selection:', prev);
          console.log('Updating index:', indexToUpdate, 'of', prev.length);
          console.log('Previous item at index:', prev[indexToUpdate]);

          // Create a completely new array with a new object at the editing index
          const updated = prev.map((item, idx) => {
            if (idx === indexToUpdate) {
              const updatedItem = {
                ...item,
                idCourse: courseId,
                courseName: basicInfo?.courseName || courseId,
                courseDetails: {
                  learningObjectives: deliveryDetails?.learningObjectives,
                  content: basicInfo?.learningContent,
                  learningHours: deliveryDetails?.learningHours,
                  modeOfDelivery: basicInfo?.modeOfDelivery,
                  lim: indicatorStandards?.limIndicator?.toString(),
                  c: indicatorStandards?.c,
                  liaIndicator: indicatorStandards?.liaIndicator || false
                }
              };
              console.log('Updated item:', updatedItem);
              return updatedItem;
            }
            return item;
          });

          console.log('Updated curriculum item at index:', updated[indexToUpdate]);
          console.log('New curriculum selection:', updated);
          return updated;
        });

        setEditingCurriculumIndex(null);
        closeCurriculumCourseModal();
        showSuccessNotification({ message: 'Course updated successfully' });
      } catch (error: any) {
        console.error('Error updating course:', error);
        showErrorNotification({
          message: error?.message || 'Failed to fetch course details'
        });
      }
    },
    [editingCurriculumIndex, closeCurriculumCourseModal]
  );

  const handleRestoreCurriculum = React.useCallback(() => {
    setCurriculumSelection(curriculumList);
  }, [curriculumList]);

  const createMasterPlanMutation = useMutation({
    mutationFn: (payload: CreateMasterPlanPayload) =>
      reviewMasterPlanService.createMasterPlan(payload),
    onSuccess: (response) => {
      showSuccessNotification({
        title: 'Master Plan Created',
        message: response?.message || 'Master plan has been created successfully.'
      });
      queryClient.invalidateQueries({ queryKey: ['masterPlanList'] });
      const searchQuery =
        jobFamilyName && jobFamilyName.trim().length > 0
          ? createSearchParams({ search: jobFamilyName.toLowerCase() }).toString()
          : '';
      navigate({
        pathname: pathnames.reviewMasterPlan,
        search: searchQuery
      });
    },
    onError: (error: any) => {
      showErrorNotification({
        message:
          error?.response?.data?.message ||
          error?.message ||
          'Unable to create master plan. Please try again.'
      });
    }
  });

  const updateMasterPlanMutation = useMutation({
    mutationFn: (payload: UpdateMasterPlanPayload) => masterPlanService.updateMasterPlan(payload),
    onSuccess: (response) => {
      showSuccessNotification({
        title: 'Master Plan Updated',
        message: response?.message || 'Master plan has been updated successfully.'
      });
      queryClient.invalidateQueries({ queryKey: ['masterPlanList'] });
      queryClient.invalidateQueries({ queryKey: ['masterPlanListByGroup'] });

      // Navigate back to detail page with appropriate query params
      if (queryJobFamilyGroup && queryYear) {
        // Navigate to grouped detail view
        queryClient.invalidateQueries({
          queryKey: ['masterPlanDetailByGroup', queryJobFamilyGroup, queryYear, queryEndYear]
        });
        const detailQueryParams = new URLSearchParams();
        detailQueryParams.append('jobFamilyGroup', queryJobFamilyGroup);
        detailQueryParams.append('year', queryYear);
        if (queryEndYear) {
          detailQueryParams.append('endYear', queryEndYear);
        }
        navigate(`${pathnames.masterPlanDetail}?${detailQueryParams.toString()}`);
      } else if (queryJobFamily && queryYear) {
        // Navigate to original detail view (backward compatibility)
        queryClient.invalidateQueries({
          queryKey: ['masterPlanDetail', queryJobFamily, queryYear]
        });
        const detailQueryParams = new URLSearchParams();
        detailQueryParams.append('jobfamily', queryJobFamily);
        detailQueryParams.append('year', queryYear);
        navigate(`${pathnames.masterPlanDetail}?${detailQueryParams.toString()}`);
      } else if (currentProgramId) {
        queryClient.invalidateQueries({ queryKey: ['masterPlanDetail', currentProgramId] });
        navigate(pathnames.masterPlanDetail.replace(':id', currentProgramId));
      } else {
        navigate(pathnames.reviewMasterPlan);
      }
    },
    onError: (error: any) => {
      showErrorNotification({
        message:
          error?.response?.data?.message ||
          error?.message ||
          'Unable to update master plan. Please try again.'
      });
    }
  });

  const handleReindexCourses = React.useCallback((courses: SelectedCourse[]) => {
    return courses.map((course, index) => ({ ...course, courseSequence: index + 1 }));
  }, []);

  React.useEffect(() => {
    if (!isEditMode || !initialData) {
      return;
    }

    let isCancelled = false;

    const loadCourses = async () => {
      setIsPrefilling(true);
      try {
        let shouldNotifyError = false;
        const coursesWithDetails = await Promise.all(
          initialData.courses.map(async (course) => {
            try {
              const response = await courseService.detail(course.courseId);
              const detailData = response.data?.data;
              const basicInfo = detailData?.basicInformation;
              const auditInfo = detailData?.auditInfo;
              const checkInHistory = detailData?.checkInOutHistory?.[0];

              const enrichedCourse: SelectedCourse = {
                courseId: course.courseId,
                courseName: basicInfo?.courseName || course.courseId,
                courseSequence: course.courseSequence,
                courseCode: basicInfo?.courseCode,
                courseCategory: basicInfo?.courseCategory,
                status: detailData?.status,
                competencyCategory: basicInfo?.competencyCategory,
                scheduleStartDate: basicInfo?.startDateTime || null,
                scheduleEndDate: basicInfo?.endDateTime || null,
                createdAt: auditInfo?.createdAt,
                createdBy: auditInfo?.createdBy,
                checkInByName: checkInHistory?.name,
                checkInDateTime: checkInHistory?.checkInDateTime || null,
                checkOutByName: checkInHistory?.name,
                checkOutDateTime: checkInHistory?.checkOutDateTime || null,
                isRequired: course.isRequired ? 1 : 0
              };
              return enrichedCourse;
            } catch {
              shouldNotifyError = true;
              const fallbackCourse: SelectedCourse = {
                courseId: course.courseId,
                courseName: course.courseId,
                courseSequence: course.courseSequence,
                isRequired: course.isRequired ? 1 : 0
              };
              return fallbackCourse;
            }
          })
        );

        if (!isCancelled) {
          setSelectedCourses(handleReindexCourses(coursesWithDetails));
          if (shouldNotifyError) {
            showErrorNotification({
              message:
                'Some course details could not be loaded. Showing fallback information instead.'
            });
          }
        }
      } finally {
        if (!isCancelled) {
          setIsPrefilling(false);
        }
      }
    };

    loadCourses();

    return () => {
      isCancelled = true;
    };
  }, [handleReindexCourses, initialData, isEditMode]);

  const handleAddCourses = React.useCallback(
    (courses: CourseSelectionPayload[]) => {
      setSelectedCourses((prev) => {
        const existingIds = new Set(prev.map((item) => item.courseId));
        const nextCourses = [
          ...prev,
          ...courses
            .filter((course) => !existingIds.has(course.courseId))
            .map((course, index) => ({
              courseId: course.courseId,
              courseName: course.courseName,
              courseSequence: prev.length + index + 1,
              courseCode: course.courseCode,
              courseCategory: course.courseCategory,
              status: course.status,
              competencyCategory: course.competencyCategory,
              scheduleStartDate: course.scheduleStartDate,
              scheduleEndDate: course.scheduleEndDate,
              createdAt: course.createdAt,
              createdBy: course.createdBy,
              checkInByName: course.checkInByName,
              checkInDateTime: course.checkInDateTime,
              checkOutByName: course.checkOutByName,
              checkOutDateTime: course.checkOutDateTime,
              isRequired: 1
            }))
        ];
        return handleReindexCourses(nextCourses);
      });
      closeCourseModal();
    },
    [closeCourseModal, handleReindexCourses]
  );

  const handleRemoveCourse = (courseId: string) => {
    setSelectedCourses((prev) =>
      handleReindexCourses(prev.filter((item) => item.courseId !== courseId))
    );
  };

  const handleCourseDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    setSelectedCourses((prev) => {
      const updated = [...prev];
      const [moved] = updated.splice(result.source.index, 1);
      updated.splice(result.destination!.index, 0, moved);
      return handleReindexCourses(updated);
    });
  };

  const handleToggleCourseRequired = React.useCallback((courseId: string, checked: boolean) => {
    setSelectedCourses((prev) =>
      prev.map((course) =>
        course.courseId === courseId ? { ...course, isRequired: checked ? 1 : 0 } : course
      )
    );
  }, []);

  // Calculate competency count per job position
  const jobPositionCompetencyCount = React.useMemo(() => {
    const jobPositionsByCompetency = curriculumSelection.map((curriculum) => ({
      competencyName: curriculum.competencyName,
      jobPositions: (curriculum as any).jobPositions || null,
      jobPositionId: curriculum.jobPositionId || null,
      jobPositionName: curriculum.jobPositionName || null
    }));
    console.log('Job Positions per Competency:', jobPositionsByCompetency);

    const countMap = new Map<
      string,
      { name: string; competencies: Set<string>; jobBand: string }
    >();

    curriculumSelection.forEach((curriculum) => {
      // Handle jobPositions array
      if ((curriculum as any).jobPositions && Array.isArray((curriculum as any).jobPositions)) {
        const positions = (curriculum as any).jobPositions;

        // If positions array is empty, skip this curriculum
        if (positions.length === 0) {
          return;
        }

        positions.forEach((pos: any) => {
          let positionKey: string;
          let positionName: string;

          if (typeof pos === 'object' && pos !== null) {
            positionName = pos.name ? String(pos.name) : String(pos.id || pos);
            // Use name as the unique key since multiple positions can have the same ID
            positionKey = positionName;
          } else {
            positionName = String(pos);
            positionKey = String(pos);
          }

          if (!countMap.has(positionKey)) {
            countMap.set(positionKey, {
              name: positionName,
              competencies: new Set(),
              jobBand: curriculum.jobBand || ''
            });
          }
          countMap.get(positionKey)!.competencies.add(curriculum.competencyName);
        });
      }
      // Fallback to single jobPositionId/jobPositionName
      else if (curriculum.jobPositionId || curriculum.jobPositionName) {
        const positionId = curriculum.jobPositionId || curriculum.jobPositionName || 'unknown';
        const positionName = curriculum.jobPositionName || 'Unknown Position';

        if (!countMap.has(positionId)) {
          countMap.set(positionId, {
            name: positionName,
            competencies: new Set(),
            jobBand: curriculum.jobBand || ''
          });
        }
        countMap.get(positionId)!.competencies.add(curriculum.competencyName);
      }
    });

    // Convert to array and sort by competency count descending
    return Array.from(countMap.entries())
      .map(([id, data]) => ({
        id,
        name: data.name,
        count: data.competencies.size,
        competencies: Array.from(data.competencies),
        jobBand: data.jobBand
      }))
      .sort((a, b) => b.count - a.count);
  }, [curriculumSelection]);

  const columns = React.useMemo(
    () => [
      {
        heading: 'Competency Type',
        props: () => 'Technical Competency', // All from DACUM are Technical Competency
        sortable: true,
        active: true,
        id: 'competencyType'
      },
      {
        heading: 'Business Unit Name',
        props: 'businessUnitName',
        sortable: true,
        active: true,
        id: 'businessUnitName'
      },
      {
        heading: 'Job Family Name',
        props: (_row: CurriculumMappingRecord) => jobFamilyName || '-',
        sortable: true,
        active: true,
        id: 'jobFamilyName'
      },
      {
        heading: 'School Name',
        props: () => 'School of Technical Competency', // Default school name
        sortable: true,
        active: true,
        id: 'schoolName'
      },
      {
        heading: 'Job Band',
        props: 'jobBand',
        sortable: true,
        active: true,
        id: 'jobBand'
      },
      {
        heading: 'Job Position',
        props: (row: CurriculumMappingRecord) => {
          // Check if jobPositions array exists (from API response)
          if (
            (row as any).jobPositions &&
            Array.isArray((row as any).jobPositions) &&
            (row as any).jobPositions.length > 0
          ) {
            const positions = (row as any).jobPositions;
            // If multiple positions, show them as a list
            if (positions.length > 1) {
              return (
                <Stack gap={2}>
                  {positions.map((pos: any, index: number) => (
                    <Text key={index} size='sm'>
                      {index + 1}. {typeof pos === 'object' && pos?.name ? pos.name : String(pos)}
                    </Text>
                  ))}
                </Stack>
              );
            }
            // Single position
            const firstPos = positions[0];
            return typeof firstPos === 'object' && firstPos?.name
              ? firstPos.name
              : String(firstPos);
          }
          // Fallback to jobPositionName string
          return row.jobPositionName || '-';
        },
        sortable: true,
        active: true,
        id: 'jobPositionName'
      },
      {
        heading: 'Competency Name',
        props: 'competencyName',
        sortable: true,
        active: true,
        id: 'competencyName'
      },
      {
        heading: 'Competency Description',
        props: (row: ExtendedCurriculumRecord) => row.competencyDescription || '-',
        sortable: true,
        active: true,
        id: 'competencyDescription'
      },
      {
        heading: 'Category',
        props: 'category',
        sortable: true,
        active: true,
        id: 'category'
      },
      {
        heading: 'Level',
        props: 'level',
        sortable: true,
        active: true,
        id: 'level'
      },
      {
        heading: 'Year',
        props: (row: CurriculumMappingRecord) => row.year?.toString() || '-',
        sortable: true,
        active: true,
        id: 'year'
      },
      {
        heading: 'Course Name',
        props: (row: ExtendedCurriculumRecord) => {
          const allCourses = (row as any).courses as
            | Array<{ courseId: string; courseName: string }>
            | undefined;
          if (allCourses && allCourses.length > 1) {
            return (
              <Stack gap={2}>
                {allCourses.map((c, index) => (
                  <Text key={c.courseId} size='sm'>
                    {index + 1}.{' '}
                    <Anchor
                      href={`/app/lcms/courses/${c.courseId}`}
                      target='_blank'
                      rel='noopener noreferrer'
                      size='sm'
                    >
                      {c.courseName}
                    </Anchor>
                  </Text>
                ))}
              </Stack>
            );
          }
          return row.courseName && row.idCourse ? (
            <Anchor
              href={`/app/lcms/courses/${row.idCourse}`}
              target='_blank'
              rel='noopener noreferrer'
              size='sm'
            >
              {row.courseName}
            </Anchor>
          ) : (
            row.courseName || '-'
          );
        },
        sortable: false,
        active: true,
        id: 'courseName'
      },

      {
        heading: 'Actions',
        props: (_row: ExtendedCurriculumRecord, index: number) => (
          <Group gap='xs'>
            <ActionIcon
              variant='subtle'
              color='blue'
              size='sm'
              onClick={() => handleEditCurriculumCourse(index)}
              title='Edit Course'
            >
              <IconEdit size={16} />
            </ActionIcon>
            <ActionIcon
              variant='subtle'
              color='red'
              size='sm'
              onClick={() => handleRemoveCurriculum(_row)}
              title='Remove'
            >
              <IconTrash size={16} />
            </ActionIcon>
          </Group>
        ),
        sortable: false,
        active: true,
        id: 'actions'
      }
    ],
    [handleRemoveCurriculum, handleEditCurriculumCourse, jobFamilyName]
  );

  const onSubmit = async (values: CreateMasterPlanFormValues) => {
    if (!jobFamilyName) {
      showErrorNotification({
        message: 'Job family information is missing. Please go back and try again.'
      });
      return;
    }

    if (!managementLevel) {
      showErrorNotification({
        message: 'Job Band is missing. Please select a job band before creating a master plan.'
      });
      return;
    }

    if (!curriculumSelection || curriculumSelection.length === 0) {
      showErrorNotification({
        message: 'Curriculum list is empty. Please review the curriculum mapping filters.'
      });
      return;
    }

    // Validate competency count per job position
    const maxCompetencies = managementLevel === 'Officer' || managementLevel === 'Staff' ? 7 : 2;
    const violatingPositions: string[] = [];

    jobPositionCompetencyCount.forEach((position) => {
      if (position.count > maxCompetencies) {
        violatingPositions.push(`${position.name} (${position.count} competencies)`);
      }
    });

    if (violatingPositions.length > 0) {
      showErrorNotification({
        title: 'Competency Limit Exceeded',
        message: `For job band "${managementLevel}", each position can have maximum ${maxCompetencies} competencies. The following positions exceed this limit: ${violatingPositions.join(', ')}`
      });
      return;
    }

    // Send empty array if no courses selected
    const coursesPayload: MasterPlanCoursePayload[] = selectedCourses.map((course, index) => ({
      courseId: course.courseId,
      courseSequence: index + 1,
      isRequired: course.isRequired ?? 1
    }));

    const curriculumPayload = curriculumSelection.map((curriculum) => {
      const payload: any = {
        competencyId: curriculum.competencyId,
        businessUnitId: curriculum.businessUnitId,
        jobPositionId: curriculum.jobPositionId || '',
        jobBand: curriculum.jobBand || '',
        competencyName: curriculum.competencyName,
        competencyDescription: curriculum.competencyDescription || '',
        category: curriculum.category,
        level: curriculum.level,
        description: curriculum.description
      };

      const allCourses = (curriculum as any).courses as
        | Array<{ courseId: string; courseName: string }>
        | undefined;

      if (allCourses && allCourses.length > 0) {
        payload.listCourse = allCourses.map((c: any) => ({
          courseId: c.courseId,
          courseName: c.courseName || '',
          learningObjectives: c.learningObjectives || '',
          content: c.content || '',
          learningHours: c.learningHours || 0,
          modeOfDelivery: c.modeOfDelivery || '',
          LIM: c.LIM ?? false,
          C: c.C || '',
          LIA: c.LIA ?? false
        }));
        // If there is only one course and we have full courseDetails from the edit enrichment, use them
        if (allCourses.length === 1 && curriculum.idCourse && curriculum.courseDetails) {
          payload.listCourse = [
            {
              courseId: curriculum.idCourse,
              courseName: curriculum.courseName || '',
              learningObjectives: curriculum.courseDetails.learningObjectives || '',
              content: curriculum.courseDetails.content || '',
              learningHours: curriculum.courseDetails.learningHours || 0,
              modeOfDelivery: curriculum.courseDetails.modeOfDelivery || '',
              LIM:
                curriculum.courseDetails.lim === 'true' ||
                curriculum.courseDetails.lim === 'True' ||
                curriculum.courseDetails.lim === '1',
              C: curriculum.courseDetails.c || '',
              LIA: curriculum.courseDetails.liaIndicator || false
            }
          ];
        }
      } else if (curriculum.idCourse) {
        if (curriculum.courseDetails) {
          payload.listCourse = [
            {
              courseId: curriculum.idCourse,
              courseName: curriculum.courseName || '',
              learningObjectives: curriculum.courseDetails.learningObjectives || '',
              content: curriculum.courseDetails.content || '',
              learningHours: curriculum.courseDetails.learningHours || 0,
              modeOfDelivery: curriculum.courseDetails.modeOfDelivery || '',
              LIM:
                curriculum.courseDetails.lim === 'true' ||
                curriculum.courseDetails.lim === 'True' ||
                curriculum.courseDetails.lim === '1',
              C: curriculum.courseDetails.c || '',
              LIA: curriculum.courseDetails.liaIndicator || false
            }
          ];
        } else {
          payload.listCourse = [
            {
              courseId: curriculum.idCourse,
              courseName: curriculum.courseName || '',
              learningObjectives: '',
              content: '',
              learningHours: 0,
              modeOfDelivery: '',
              LIM: false,
              C: '',
              LIA: false
            }
          ];
        }
      } else {
        payload.listCourse = [];
      }

      return payload;
    });

    const payload: CreateMasterPlanPayload = {
      jobFamily: jobFamilyName,
      programName: values.programName,
      jobBand: {
        name: managementLevel
      },
      description: values.description,
      keyAction: values.keyAction,
      businessUnitId: businessUnitId || undefined,
      year: parseInt(values.tahun),
      endYear: parseInt(values.endYear),
      courses: coursesPayload,
      curriculumList: curriculumPayload,
      note: values.note || '',
      msSchoolCompetencyManagementID: isEditMode
        ? null
        : (() => {
            const schoolIdMap: Record<string, string> = Object.fromEntries(
              staticCompetencyData
                .filter((item) => item.msSchoolCompetencyManagementID)
                .map((item) => [item.competencytype, item.msSchoolCompetencyManagementID])
            );
            return schoolIdMap[competencyType] ?? msSchoolCompetencyManagementID ?? undefined;
          })()
    };

    if (jobFamilyGroupName) {
      payload.jobFamilyGroupName = jobFamilyGroupName;
    }

    if (isEditMode) {
      if (!currentProgramId) {
        showErrorNotification({
          message: 'Master plan identifier is missing. Please reload the page and try again.'
        });
        return;
      }

      const updatePayload: UpdateMasterPlanPayload = {
        ...payload,
        msDACUMProgramID: currentProgramId
      };

      updateMasterPlanMutation.mutate(updatePayload);
      return;
    }

    // console.log('Submitting create master plan payload:', payload);

    createMasterPlanMutation.mutate(payload);
  };

  const isLoading =
    loadingCurriculum ||
    createMasterPlanMutation.isPending ||
    updateMasterPlanMutation.isPending ||
    isSubmitting ||
    isPrefilling;
  const selectionChanged = curriculumSelection.length !== curriculumList.length;

  const submitLabel = isEditMode ? 'Update Master Plan' : 'Create Master Plan';

  const handleCancel = () => {
    navigate(-1);
  };

  const canDisplayCurriculumList = isEditMode || isFilterReady;

  return (
    <Card>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack gap='lg'>
          <Stack gap='xs'>
            <Text fw={600}>Master Plan Context</Text>
            <Group gap='xs'>
              <Badge color='blue' variant='light'>
                Business Unit: {businessUnitName || businessUnitId || '-'}
              </Badge>
              <Badge color='green' variant='light'>
                Job Family: {jobFamilyName || jobFamilyId || '-'}
              </Badge>
              <Badge color='grape' variant='light'>
                Job Band: {managementLevel || '-'}
              </Badge>
              {jobProfileId && (
                <Badge color='teal' variant='light'>
                  Job Profile: {jobProfileId}
                </Badge>
              )}
            </Group>
          </Stack>

          {!isEditMode && !msSchoolCompetencyManagementIDFromState && (
            <Alert
              icon={<IconAlertTriangle size={18} />}
              title='School Competency Not Mapped'
              color='orange'
              variant='light'
            >
              This competency does not have a school competency mapped. School competency mapping is{' '}
              <strong>required</strong> for master plan submission. Please map a school competency
              before proceeding.
            </Alert>
          )}

          <Stack gap='md'>
            <TextInput
              label='Curriculum Name'
              placeholder='Enter curriculum name'
              {...register('programName', { required: 'Curriculum name is required' })}
              error={errors.programName?.message}
            />

            <Textarea
              label='Description'
              placeholder='Enter description'
              minRows={4}
              autosize
              {...register('description', { required: 'Description is required' })}
              error={errors.description?.message}
            />

            <Controller
              name='tahun'
              control={control}
              rules={{ required: 'Year is required' }}
              render={({ field }) => (
                <Select
                  {...field}
                  label='Year'
                  placeholder='Select year'
                  data={(() => {
                    const currentYear = new Date().getFullYear();
                    const years = [];
                    for (let i = currentYear - 2; i <= currentYear + 5; i++) {
                      years.push({ value: i.toString(), label: i.toString() });
                    }
                    return years;
                  })()}
                  error={errors.tahun?.message}
                />
              )}
            />

            <Controller
              name='endYear'
              control={control}
              rules={{ required: 'End year is required' }}
              render={({ field }) => (
                <Select
                  {...field}
                  label='End Year'
                  placeholder='Select end year'
                  data={(() => {
                    const startYear = selectedYear
                      ? parseInt(selectedYear)
                      : new Date().getFullYear();
                    const years = [];
                    for (let i = startYear; i <= startYear + 3; i++) {
                      years.push({ value: i.toString(), label: i.toString() });
                    }
                    return years;
                  })()}
                  error={errors.endYear?.message}
                />
              )}
            />

            <input type='hidden' {...register('keyAction')} />
          </Stack>

          <Stack gap='sm' style={{ display: 'none' }}>
            <Group justify='space-between'>
              <Text fw={600}>Selected Courses</Text>
              <Button
                leftSection={<IconPlus size={16} />}
                onClick={openCourseModal}
                variant='light'
                disabled={isLoading}
              >
                Select Courses
              </Button>
            </Group>

            {selectedCourses.length === 0 ? (
              <Text size='sm' c='dimmed'>
                No courses selected yet.
              </Text>
            ) : (
              <DragDropContext onDragEnd={handleCourseDragEnd}>
                <Droppable droppableId='selected-courses'>
                  {(droppableProvided) => (
                    <ScrollArea offsetScrollbars>
                      <Table striped withTableBorder highlightOnHover>
                        <Table.Thead>
                          <Table.Tr>
                            <Table.Th style={{ width: 90 }}>#</Table.Th>
                            <Table.Th>Course</Table.Th>
                            <Table.Th>Course Code</Table.Th>
                            <Table.Th>Category</Table.Th>
                            <Table.Th>Status</Table.Th>
                            <Table.Th>Competency Category</Table.Th>
                            <Table.Th>Schedule</Table.Th>
                            <Table.Th>Created</Table.Th>
                            <Table.Th>Check In</Table.Th>
                            <Table.Th>Check Out</Table.Th>
                            <Table.Th style={{ width: 110 }}>Required</Table.Th>
                            <Table.Th style={{ width: 80 }}>Actions</Table.Th>
                          </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody
                          ref={droppableProvided.innerRef}
                          {...droppableProvided.droppableProps}
                        >
                          {selectedCourses.map((course, index) => (
                            <Draggable
                              key={course.courseId}
                              draggableId={course.courseId}
                              index={index}
                            >
                              {(draggableProvided, snapshot) => (
                                <Table.Tr
                                  ref={draggableProvided.innerRef}
                                  {...draggableProvided.draggableProps}
                                  style={{
                                    ...draggableProvided.draggableProps.style,
                                    backgroundColor: snapshot.isDragging ? '#f3f5ff' : undefined
                                  }}
                                >
                                  <Table.Td>
                                    <Group gap='xs' align='center'>
                                      <ActionIcon
                                        variant='subtle'
                                        color='gray'
                                        {...draggableProvided.dragHandleProps}
                                      >
                                        <IconGripVertical size={16} />
                                      </ActionIcon>
                                      <Text size='sm'>{index + 1}</Text>
                                    </Group>
                                  </Table.Td>
                                  <Table.Td>
                                    <Stack gap={2} align='flex-start'>
                                      <Text fw={600} size='md'>
                                        {course.courseName}
                                      </Text>
                                      <Text size='xs' c='dimmed'>
                                        {course.courseId}
                                      </Text>
                                    </Stack>
                                  </Table.Td>
                                  <Table.Td>{course.courseCode || '-'}</Table.Td>
                                  <Table.Td>{course.courseCategory || '-'}</Table.Td>
                                  <Table.Td>{course.status || '-'}</Table.Td>
                                  <Table.Td>{course.competencyCategory || '-'}</Table.Td>
                                  <Table.Td>
                                    <Text size='sm'>
                                      {formatScheduleRange(
                                        course.scheduleStartDate,
                                        course.scheduleEndDate
                                      )}
                                    </Text>
                                  </Table.Td>
                                  <Table.Td>
                                    <Stack gap={2} align='flex-start'>
                                      <Text size='sm'>{course.createdBy || '-'}</Text>
                                      <Text size='xs' c='dimmed'>
                                        {formatDateTime(course.createdAt)}
                                      </Text>
                                    </Stack>
                                  </Table.Td>
                                  <Table.Td>
                                    <Text size='sm'>
                                      {formatCheckInfo(
                                        course.checkInByName,
                                        course.checkInDateTime
                                      )}
                                    </Text>
                                  </Table.Td>
                                  <Table.Td>
                                    <Text size='sm'>
                                      {formatCheckInfo(
                                        course.checkOutByName,
                                        course.checkOutDateTime
                                      )}
                                    </Text>
                                  </Table.Td>
                                  <Table.Td>
                                    <Switch
                                      size='sm'
                                      checked={course.isRequired === 1}
                                      onChange={(event) =>
                                        handleToggleCourseRequired(
                                          course.courseId,
                                          event.currentTarget.checked
                                        )
                                      }
                                      aria-label={`Mark ${course.courseName} as ${
                                        course.isRequired === 1 ? 'required' : 'optional'
                                      }`}
                                    />
                                  </Table.Td>
                                  <Table.Td>
                                    <Group justify='flex-end'>
                                      <ActionIcon
                                        variant='light'
                                        color='red'
                                        size='sm'
                                        onClick={() => handleRemoveCourse(course.courseId)}
                                      >
                                        <IconTrash size={16} />
                                      </ActionIcon>
                                    </Group>
                                  </Table.Td>
                                </Table.Tr>
                              )}
                            </Draggable>
                          ))}
                          {droppableProvided.placeholder}
                        </Table.Tbody>
                      </Table>
                    </ScrollArea>
                  )}
                </Droppable>
              </DragDropContext>
            )}
          </Stack>

          <Stack gap='sm'>
            <Text fw={600}>Note</Text>
            <Textarea
              placeholder='Enter additional notes (optional)'
              minRows={3}
              autosize
              {...register('note')}
              error={errors.note?.message}
            />
          </Stack>

          <Stack gap='sm'>
            <Group justify='space-between'>
              <Text fw={600}>Curriculum List</Text>
              <Group gap='xs'>
                {canDisplayCurriculumList && (
                  <Button
                    leftSection={<IconPlus size={16} />}
                    onClick={openCurriculumModal}
                    variant='light'
                    size='xs'
                    disabled={isLoading}
                  >
                    Add Curriculum
                  </Button>
                )}
                {selectionChanged && (
                  <Button variant='subtle' size='xs' onClick={handleRestoreCurriculum}>
                    Restore removed
                  </Button>
                )}
              </Group>
            </Group>
            {canDisplayCurriculumList ? (
              curriculumSelection.length > 0 ? (
                <DataTables
                  columns={columns}
                  data={curriculumSelection}
                  loading={loadingCurriculum}
                  withBulkData
                  totalData={curriculumSelection.length}
                  emptyMessageTitle='No curriculum data'
                  emptyMessageDescription='Adjust the selection to retrieve curriculum mapping data.'
                />
              ) : (
                <Text size='sm' c='dimmed'>
                  All curriculum entries for this context have been removed. Use “Restore removed”
                  to bring them back.
                </Text>
              )
            ) : (
              <Text size='sm' c='dimmed'>
                Curriculum mapping filters are incomplete.
              </Text>
            )}
          </Stack>

          {/* Job Position Competency Indicator */}
          {canDisplayCurriculumList && jobPositionCompetencyCount.length > 0 && (
            <Card withBorder shadow='sm' radius='md' p='md'>
              <Stack gap='sm'>
                <div>
                  <Group justify='space-between' align='center'>
                    <div>
                      <Text fw={600} size='md' c='dark'>
                        Competency Overview
                      </Text>
                      <Text size='xs' c='dimmed' mt={2}>
                        {jobPositionCompetencyCount.length} position
                        {jobPositionCompetencyCount.length > 1 ? 's' : ''} • Max{' '}
                        {managementLevel === 'Officer' || managementLevel === 'Staff' ? '7' : '2'}
                      </Text>
                    </div>
                  </Group>
                  <Divider my='sm' />
                </div>

                <Accordion
                  variant='contained'
                  radius='sm'
                  chevronPosition='right'
                  styles={{
                    item: {
                      border: '1px solid #e9ecef',
                      marginBottom: '6px',
                      backgroundColor: '#fff'
                    },
                    control: {
                      padding: '8px 12px',
                      '&:hover': {
                        backgroundColor: '#f8f9fa'
                      }
                    },
                    content: {
                      padding: '0'
                    }
                  }}
                >
                  {(() => {
                    const maxLimit =
                      managementLevel === 'Officer' || managementLevel === 'Staff' ? 7 : 2;
                    const exceedingPositions = jobPositionCompetencyCount.filter(
                      (p) => p.count > maxLimit
                    );
                    const validPositions = jobPositionCompetencyCount.filter(
                      (p) => p.count <= maxLimit
                    );

                    return (
                      <>
                        {exceedingPositions.length > 0 && (
                          <Accordion.Item
                            value='exceeding'
                            style={{ borderLeft: '3px solid #fa5252' }}
                          >
                            <Accordion.Control>
                              <Group justify='space-between' wrap='nowrap' pr='sm'>
                                <Text fw={600} size='sm' c='red'>
                                  Exceeds Limit ({exceedingPositions.length})
                                </Text>
                                <Badge color='red' size='sm' variant='filled'>
                                  {exceedingPositions.reduce(
                                    (sum, p) => sum + (p.count - maxLimit),
                                    0
                                  )}{' '}
                                  over
                                </Badge>
                              </Group>
                            </Accordion.Control>
                            <Accordion.Panel>
                              <Stack gap={4}>
                                {exceedingPositions.map((position) => (
                                  <Accordion
                                    key={position.name}
                                    variant='separated'
                                    radius='sm'
                                    chevronPosition='right'
                                    styles={{
                                      item: {
                                        border: '1px solid #ffe0e0',
                                        backgroundColor: '#fff5f5'
                                      },
                                      control: {
                                        padding: '8px 10px',
                                        '&:hover': {
                                          backgroundColor: '#ffe0e0'
                                        }
                                      },
                                      content: {
                                        padding: '8px 10px'
                                      }
                                    }}
                                  >
                                    <Accordion.Item value={position.name}>
                                      <Accordion.Control>
                                        <Group justify='space-between' wrap='nowrap' pr='sm'>
                                          <Text
                                            fw={500}
                                            size='xs'
                                            lineClamp={1}
                                            style={{ flex: 1 }}
                                          >
                                            {position.name}
                                          </Text>
                                          <Group gap='xs' wrap='nowrap'>
                                            <Badge size='sm' color='red' variant='filled'>
                                              {position.count}
                                            </Badge>
                                            <Text size='xs' c='dimmed'>
                                              / {maxLimit}
                                            </Text>
                                          </Group>
                                        </Group>
                                      </Accordion.Control>
                                      <Accordion.Panel>
                                        <Stack gap={4}>
                                          {position.competencies.map((competency, idx) => (
                                            <Group
                                              key={idx}
                                              gap='xs'
                                              wrap='nowrap'
                                              p={4}
                                              style={{
                                                backgroundColor:
                                                  idx % 2 === 0 ? '#fff' : 'transparent',
                                                borderRadius: '3px'
                                              }}
                                            >
                                              <Badge
                                                color='red'
                                                size='xs'
                                                circle
                                                variant='filled'
                                                style={{ minWidth: '18px' }}
                                              >
                                                {idx + 1}
                                              </Badge>
                                              <Text size='xs' style={{ flex: 1 }}>
                                                {competency}
                                              </Text>
                                            </Group>
                                          ))}
                                        </Stack>
                                      </Accordion.Panel>
                                    </Accordion.Item>
                                  </Accordion>
                                ))}
                              </Stack>
                            </Accordion.Panel>
                          </Accordion.Item>
                        )}

                        {validPositions.length > 0 && (
                          <Accordion.Item value='valid' style={{ borderLeft: '3px solid #339af0' }}>
                            <Accordion.Control>
                              <Group justify='space-between' wrap='nowrap' pr='sm'>
                                <Text fw={600} size='sm' c='blue'>
                                  Within Limit ({validPositions.length})
                                </Text>
                                <Badge color='blue' size='sm' variant='filled'>
                                  Valid
                                </Badge>
                              </Group>
                            </Accordion.Control>
                            <Accordion.Panel>
                              <Stack gap={4}>
                                {validPositions.map((position) => {
                                  const isNearLimit = position.count === maxLimit;
                                  return (
                                    <Accordion
                                      key={position.name}
                                      variant='separated'
                                      radius='sm'
                                      chevronPosition='right'
                                      styles={{
                                        item: {
                                          border: `1px solid ${isNearLimit ? '#fff3bf' : '#e7f5ff'}`,
                                          backgroundColor: isNearLimit ? '#fff9db' : '#f0f7ff'
                                        },
                                        control: {
                                          padding: '8px 10px',
                                          '&:hover': {
                                            backgroundColor: isNearLimit ? '#ffec99' : '#d0ebff'
                                          }
                                        },
                                        content: {
                                          padding: '8px 10px'
                                        }
                                      }}
                                    >
                                      <Accordion.Item value={position.name}>
                                        <Accordion.Control>
                                          <Group justify='space-between' wrap='nowrap' pr='sm'>
                                            <Text
                                              fw={500}
                                              size='xs'
                                              lineClamp={1}
                                              style={{ flex: 1 }}
                                            >
                                              {position.name}
                                            </Text>
                                            <Group gap='xs' wrap='nowrap'>
                                              <Badge
                                                size='sm'
                                                color={isNearLimit ? 'yellow' : 'blue'}
                                                variant='filled'
                                              >
                                                {position.count}
                                              </Badge>
                                              <Text size='xs' c='dimmed'>
                                                / {maxLimit}
                                              </Text>
                                              {isNearLimit && (
                                                <Badge color='yellow' size='xs' variant='light'>
                                                  At Limit
                                                </Badge>
                                              )}
                                            </Group>
                                          </Group>
                                        </Accordion.Control>
                                        <Accordion.Panel>
                                          <Stack gap={4}>
                                            {position.competencies.map((competency, idx) => (
                                              <Group
                                                key={idx}
                                                gap='xs'
                                                wrap='nowrap'
                                                p={4}
                                                style={{
                                                  backgroundColor:
                                                    idx % 2 === 0 ? '#fff' : 'transparent',
                                                  borderRadius: '3px'
                                                }}
                                              >
                                                <Badge
                                                  color={isNearLimit ? 'yellow' : 'blue'}
                                                  size='xs'
                                                  circle
                                                  variant='filled'
                                                  style={{ minWidth: '18px' }}
                                                >
                                                  {idx + 1}
                                                </Badge>
                                                <Text size='xs' style={{ flex: 1 }}>
                                                  {competency}
                                                </Text>
                                              </Group>
                                            ))}
                                          </Stack>
                                        </Accordion.Panel>
                                      </Accordion.Item>
                                    </Accordion>
                                  );
                                })}
                              </Stack>
                            </Accordion.Panel>
                          </Accordion.Item>
                        )}
                      </>
                    );
                  })()}
                </Accordion>
              </Stack>
            </Card>
          )}

          <Group justify='flex-end'>
            <Button variant='outline' onClick={handleCancel} disabled={isLoading}>
              Cancel
            </Button>
            <Button type='submit' loading={isLoading}>
              {submitLabel}
            </Button>
          </Group>
        </Stack>
      </form>

      <CourseSelectionModal
        opened={courseModalOpened}
        onClose={closeCourseModal}
        onSubmit={handleAddCourses}
        selectedCourses={selectedCourses}
      />

      <CurriculumSelectionModal
        opened={curriculumModalOpened}
        onClose={closeCurriculumModal}
        onSubmit={async (items) => {
          const existingKeys = new Set(curriculumSelection.map(buildCurriculumKey));
          const newItems = items.filter((item) => !existingKeys.has(buildCurriculumKey(item)));

          // Fetch course details for new items that have courses
          const enrichedItems = await Promise.all(
            newItems.map(async (item) => {
              if (item.idCourse) {
                try {
                  const response = await courseService.detail(item.idCourse);
                  const detailData = response.data?.data;
                  const basicInfo = detailData?.basicInformation;
                  const deliveryDetails = detailData?.deliveryDetails;
                  const indicatorStandards = detailData?.indicatorStandards;

                  return {
                    ...item,
                    courseDetails: {
                      learningObjectives: deliveryDetails?.learningObjectives,
                      content: basicInfo?.learningContent,
                      learningHours: deliveryDetails?.learningHours,
                      modeOfDelivery: basicInfo?.modeOfDelivery,
                      lim: indicatorStandards?.limIndicator?.toString(),
                      c: indicatorStandards?.c,
                      liaIndicator: indicatorStandards?.liaIndicator || false
                    }
                  } as ExtendedCurriculumRecord;
                } catch (error) {
                  console.error('Failed to fetch course details:', error);
                  return item as ExtendedCurriculumRecord;
                }
              }
              return item as ExtendedCurriculumRecord;
            })
          );

          setCurriculumSelection((prev) => [...prev, ...enrichedItems]);
          closeCurriculumModal();
        }}
        selectedCurriculum={curriculumSelection}
        jobFamilyId={jobFamilyId}
        jobFamilyName={jobFamilyName}
        managementLevel={managementLevel}
        businessUnitId={businessUnitId}
        businessUnitName={businessUnitName}
        competencyType={competencyType}
      />

      <CurriculumCourseSelectionModal
        opened={curriculumCourseModalOpened}
        onClose={closeCurriculumCourseModal}
        onSubmit={handleUpdateCurriculumCourse}
        curriculumContext={
          editingCurriculumIndex !== null && curriculumSelection[editingCurriculumIndex]
            ? {
                competencyName: curriculumSelection[editingCurriculumIndex].competencyName || '-',
                category: curriculumSelection[editingCurriculumIndex].category || '-',
                level: Number(curriculumSelection[editingCurriculumIndex].level) || 0,
                competencyType: competencyType || '-'
              }
            : null
        }
      />
    </Card>
  );
};

interface CourseSelectionModalProps {
  opened: boolean;
  onClose: () => void;
  onSubmit: (_courses: CourseSelectionPayload[]) => void;
  selectedCourses: SelectedCourse[];
}

const CourseSelectionModal: React.FC<CourseSelectionModalProps> = ({
  opened,
  onClose,
  onSubmit,
  selectedCourses
}) => {
  const [searchKeyword, setSearchKeyword] = React.useState('');
  const [page, setPage] = React.useState(1);
  const [selection, setSelection] = React.useState<Record<string, CourseOption>>({});

  React.useEffect(() => {
    if (opened) {
      const initialSelection: Record<string, CourseOption> = {};
      selectedCourses.forEach((course) => {
        initialSelection[course.courseId] = {
          msLCMSCourseID: course.courseId,
          courseName: course.courseName,
          courseCode: course.courseCode,
          courseCategory: course.courseCategory,
          status: course.status,
          competencyCategory: course.competencyCategory,
          scheduleStartDate: course.scheduleStartDate,
          scheduleEndDate: course.scheduleEndDate,
          createdAt: course.createdAt,
          createdBy: course.createdBy,
          checkInByName: course.checkInByName,
          checkInDateTime: course.checkInDateTime,
          checkOutByName: course.checkOutByName,
          checkOutDateTime: course.checkOutDateTime
        };
      });
      setSelection(initialSelection);
    }
  }, [opened, selectedCourses]);

  const { data, isFetching } = useQuery({
    queryKey: ['lcms-courses-selector', searchKeyword, page],
    queryFn: () =>
      courseService.list({
        search: searchKeyword,
        pageNumber: page,
        pageSize: DEFAULT_PAGE_SIZE
      }),
    placeholderData: (previousData) => previousData,
    enabled: opened
  });

  const courses: CourseOption[] = (data?.data || []).map((item: any) => ({
    msLCMSCourseID: item.msLCMSCourseID || item.courseId || item.id,
    courseName: item.courseName || item.name || '-',
    courseCode: item.courseCode || item.code,
    courseCategory: item.courseCategory || item.category,
    status: item.status,
    competencyCategory: item.competencyCategory,
    scheduleStartDate: item.schedule?.startDate ?? null,
    scheduleEndDate: item.schedule?.endDate ?? null,
    createdAt: item.createdAt,
    createdBy: item.createdBy,
    checkInByName: item.checkInBy?.name,
    checkInDateTime: item.checkInBy?.checkInDateTime,
    checkOutByName: item.checkOutBy?.name,
    checkOutDateTime: item.checkOutBy?.checkOutDateTime
  }));
  const totalPages = data?.totalPages || 1;

  const toggleSelection = (course: CourseOption) => {
    setSelection((prev) => {
      const next = { ...prev };
      if (next[course.msLCMSCourseID]) {
        delete next[course.msLCMSCourseID];
      } else {
        next[course.msLCMSCourseID] = course;
      }
      return next;
    });
  };

  const handleConfirm = () => {
    const selectedList = Object.values(selection).map((item) => ({
      courseId: item.msLCMSCourseID,
      courseName: item.courseName,
      courseCode: item.courseCode,
      courseCategory: item.courseCategory,
      status: item.status,
      competencyCategory: item.competencyCategory,
      scheduleStartDate: item.scheduleStartDate,
      scheduleEndDate: item.scheduleEndDate,
      createdAt: item.createdAt,
      createdBy: item.createdBy,
      checkInByName: item.checkInByName,
      checkInDateTime: item.checkInDateTime,
      checkOutByName: item.checkOutByName,
      checkOutDateTime: item.checkOutDateTime
    }));
    onSubmit(selectedList);
  };

  const selectedCount = Object.keys(selection).length;

  return (
    <Modal opened={opened} onClose={onClose} centered size='80%' radius='lg' shadow='lg'>
      <Stack gap='lg'>
        <Group justify='space-between' align='flex-start'>
          <Stack gap={4}>
            <Text fw={700} size='lg'>
              Select Courses
            </Text>
            <Text size='sm' c='dimmed'>
              Choose one or more courses to include in this master plan. Drag to reorder after
              adding.
            </Text>
          </Stack>
          <Badge color='indigo' variant='light'>
            Selected: {selectedCount}
          </Badge>
        </Group>

        <TextInput
          placeholder='Search courses by name, code, or category'
          value={searchKeyword}
          onChange={(event) => {
            setSearchKeyword(event.currentTarget.value);
            setPage(1);
          }}
          leftSection={<IconSearch size={16} stroke={1.6} />}
          radius='md'
          size='md'
          variant='filled'
        />

        {isFetching ? (
          <Flex justify='center' align='center' h={120}>
            <Loader size='sm' />
          </Flex>
        ) : courses.length === 0 ? (
          <Text size='sm' c='dimmed'>
            No courses found.
          </Text>
        ) : (
          <ScrollArea h={320} offsetScrollbars>
            <Table striped highlightOnHover withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th style={{ width: 70 }}>Select</Table.Th>
                  <Table.Th>Course</Table.Th>
                  <Table.Th>Course Code</Table.Th>
                  <Table.Th>Category</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Competency Category</Table.Th>
                  <Table.Th>Created</Table.Th>
                  <Table.Th>Check In</Table.Th>
                  <Table.Th>Check Out</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {courses.map((course) => {
                  const checked = Boolean(selection[course.msLCMSCourseID]);
                  return (
                    <Table.Tr key={course.msLCMSCourseID}>
                      <Table.Td>
                        <Checkbox
                          checked={checked}
                          onChange={() => toggleSelection(course)}
                          aria-label={`Select ${course.courseName}`}
                        />
                      </Table.Td>
                      <Table.Td>
                        <Stack gap={2} align='flex-start'>
                          <Text fw={600} size='md'>
                            {course.courseName}
                          </Text>
                          <Text size='xs' c='dimmed'>
                            {course.msLCMSCourseID}
                          </Text>
                        </Stack>
                      </Table.Td>
                      <Table.Td>{course.courseCode || '-'}</Table.Td>
                      <Table.Td>{course.courseCategory || '-'}</Table.Td>
                      <Table.Td>{course.status || '-'}</Table.Td>
                      <Table.Td>{course.competencyCategory || '-'}</Table.Td>

                      <Table.Td>
                        <Stack gap={2} align='flex-start'>
                          <Text size='sm'>{course.createdBy || '-'}</Text>
                          <Text size='xs' c='dimmed'>
                            {formatDateTime(course.createdAt)}
                          </Text>
                        </Stack>
                      </Table.Td>
                      <Table.Td>
                        <Text size='sm'>
                          {formatCheckInfo(course.checkInByName, course.checkInDateTime)}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size='sm'>
                          {formatCheckInfo(course.checkOutByName, course.checkOutDateTime)}
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        )}

        <Group justify='center'>
          <Pagination total={Math.max(totalPages, 1)} value={page} onChange={setPage} />
        </Group>

        <Group justify='flex-end'>
          <Button variant='default' onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={Object.keys(selection).length === 0}>
            Add Courses
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

interface CurriculumSelectionModalProps {
  opened: boolean;
  onClose: () => void;
  onSubmit: (_curriculum: CurriculumMappingRecord[]) => void;
  selectedCurriculum: CurriculumMappingRecord[];
  jobFamilyId?: string;
  jobFamilyName?: string;
  managementLevel?: string;
  businessUnitId?: string;
  businessUnitName?: string;
  competencyType?: string;
}

const CurriculumSelectionModal: React.FC<CurriculumSelectionModalProps> = ({
  opened,
  onClose,
  onSubmit,
  selectedCurriculum,
  jobFamilyId,
  jobFamilyName,
  managementLevel,
  businessUnitId,
  businessUnitName,
  competencyType = 'Technical Competency'
}) => {
  const [searchKeyword, setSearchKeyword] = React.useState('');
  const [page, setPage] = React.useState(1);
  const [selection, setSelection] = React.useState<Record<string, CurriculumMappingRecord>>({});

  React.useEffect(() => {
    if (opened) {
      const initialSelection: Record<string, CurriculumMappingRecord> = {};
      selectedCurriculum.forEach((item) => {
        const key = `${item.competencyId}-${item.category}-${item.level}-${item.jobPositionId}`;
        initialSelection[key] = item;
      });
      setSelection(initialSelection);
    }
  }, [opened, selectedCurriculum]);

  const isFilterReady = Boolean(
    (jobFamilyId || jobFamilyName) && managementLevel && (businessUnitId || businessUnitName)
  );

  const { data: competencyResponse, isFetching } = useQuery({
    queryKey: [
      'competencyManagement-curriculum-modal',
      jobFamilyId,
      jobFamilyName,
      managementLevel,
      businessUnitId,
      businessUnitName,
      competencyType,
      searchKeyword,
      page
    ],
    queryFn: async () => {
      const jobFamilyParam =
        competencyType === 'Technical Competency' ? jobFamilyName || '' : jobFamilyId || '';
      const businessUnitParam =
        competencyType === 'Technical Competency' ? businessUnitName || '' : businessUnitId || '';

      return coreCompetencyService.list({
        pageNumber: page,
        pageSize: DEFAULT_PAGE_SIZE,
        competencyType: competencyType,
        jobFamilyId: jobFamilyParam,
        managementLevel: managementLevel || '',
        businessUnitId: businessUnitParam,
        search: searchKeyword
      });
    },
    enabled: opened && isFilterReady,
    placeholderData: (previousData) => previousData
  });

  const curriculumItems: CurriculumMappingRecord[] = React.useMemo(() => {
    if (!competencyResponse?.data) return [];

    return competencyResponse.data.map((item: any) => ({
      competencyId: item.msCompetencyManagementID,
      businessUnitId: item.businessUnitId,
      businessUnitName: item.businessUnitName,
      jobPositionId: item.jobPosition || '',
      jobPositionName: item.jobPosition || '-',
      jobPositions: item.jobPositions,
      jobProfileId: undefined,
      jobBand: item.managementLevel,
      competencyName: item.competencyName,
      competencyDescription: item.categoryDescription || '',
      category: item.category,
      level: item.level.toString(),
      levelDescription: item.levelDescription || '',
      description: '',
      idCourse: item.courses && item.courses.length > 0 ? item.courses[0].courseId : null,
      courseName: item.courses && item.courses.length > 0 ? item.courses[0].courseName : null,
      courses: (item.courses || []).map((c: any) => ({
        courseId: c.courseId,
        courseName: c.courseName,
        learningObjectives: c.learningObjectives || '',
        learningHours: c.learningHours ? parseInt(c.learningHours) : 0,
        modeOfDelivery: c.modeOfDelivery || '',
        LIM: c.flaggingLIM || false,
        C: c.C || '',
        LIA: c.flaggingLIA || false
      })),
      year: item.year
    })) as CurriculumMappingRecord[];
  }, [competencyResponse]);

  const totalPages = competencyResponse?.totalPages || 1;

  const buildKey = (record: CurriculumMappingRecord) =>
    `${record.competencyId}-${record.category}-${record.level}-${record.jobPositionId}`;

  const toggleSelection = (curriculum: CurriculumMappingRecord) => {
    const key = buildKey(curriculum);
    setSelection((prev) => {
      const next = { ...prev };
      if (next[key]) {
        delete next[key];
      } else {
        next[key] = curriculum;
      }
      return next;
    });
  };

  const handleConfirm = () => {
    const selectedList = Object.values(selection);
    onSubmit(selectedList);
  };

  const selectedCount = Object.keys(selection).length;

  return (
    <Modal opened={opened} onClose={onClose} centered size='90%' radius='lg' shadow='lg'>
      <Stack gap='lg'>
        <Group justify='space-between' align='flex-start'>
          <Stack gap={4}>
            <Text fw={700} size='lg'>
              Select Curriculum Items
            </Text>
            <Text size='sm' c='dimmed'>
              Choose one or more curriculum items to add to this master plan.
            </Text>
          </Stack>
          <Badge color='indigo' variant='light'>
            Selected: {selectedCount}
          </Badge>
        </Group>

        <TextInput
          placeholder='Search by competency name, category, or level'
          value={searchKeyword}
          onChange={(event) => {
            setSearchKeyword(event.currentTarget.value);
            setPage(1);
          }}
          leftSection={<IconSearch size={16} stroke={1.6} />}
          radius='md'
          size='md'
          variant='filled'
        />

        {isFetching ? (
          <Flex justify='center' align='center' h={120}>
            <Loader size='sm' />
          </Flex>
        ) : curriculumItems.length === 0 ? (
          <Text size='sm' c='dimmed'>
            No curriculum items found.
          </Text>
        ) : (
          <ScrollArea h={400} offsetScrollbars>
            <Table striped highlightOnHover withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th style={{ width: 70 }}>Select</Table.Th>
                  <Table.Th>Competency Name</Table.Th>
                  <Table.Th>Category</Table.Th>
                  <Table.Th>Level</Table.Th>
                  <Table.Th>Job Band</Table.Th>
                  <Table.Th>Job Position</Table.Th>
                  <Table.Th>Business Unit</Table.Th>
                  <Table.Th>Course</Table.Th>
                  <Table.Th style={{ width: 80 }}>Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {curriculumItems.map((item) => {
                  const key = buildKey(item);
                  const checked = Boolean(selection[key]);
                  return (
                    <Table.Tr key={key}>
                      <Table.Td>
                        <Checkbox
                          checked={checked}
                          onChange={() => toggleSelection(item)}
                          aria-label={`Select ${item.competencyName}`}
                        />
                      </Table.Td>
                      <Table.Td>
                        <Text fw={600} size='sm'>
                          {item.competencyName}
                        </Text>
                      </Table.Td>
                      <Table.Td>{item.category}</Table.Td>
                      <Table.Td>{item.level}</Table.Td>
                      <Table.Td>{item.jobBand}</Table.Td>
                      <Table.Td>
                        {(item as any).jobPositions &&
                        Array.isArray((item as any).jobPositions) &&
                        (item as any).jobPositions.length > 0 ? (
                          <Stack gap={2}>
                            {(item as any).jobPositions.map((pos: any, index: number) => (
                              <Text key={index} size='sm'>
                                {typeof pos === 'object' && pos?.name ? pos.name : String(pos)}
                              </Text>
                            ))}
                          </Stack>
                        ) : (
                          item.jobPositionName
                        )}
                      </Table.Td>
                      <Table.Td>{item.businessUnitName}</Table.Td>
                      <Table.Td>
                        {(() => {
                          const allCourses = (item as any).courses as
                            | Array<{ courseId: string; courseName: string }>
                            | undefined;
                          if (allCourses && allCourses.length > 1) {
                            return (
                              <Stack gap={2}>
                                {allCourses.map((c, idx) => (
                                  <Text key={c.courseId} size='sm'>
                                    {idx + 1}.{' '}
                                    <Anchor
                                      href={`/app/lcms/courses/${c.courseId}`}
                                      target='_blank'
                                      rel='noopener noreferrer'
                                      size='sm'
                                    >
                                      {c.courseName}
                                    </Anchor>
                                  </Text>
                                ))}
                              </Stack>
                            );
                          }
                          return item.courseName && item.idCourse ? (
                            <Anchor
                              href={`/app/lcms/courses/${item.idCourse}`}
                              target='_blank'
                              rel='noopener noreferrer'
                              size='sm'
                            >
                              {item.courseName}
                            </Anchor>
                          ) : (
                            item.courseName || '-'
                          );
                        })()}
                      </Table.Td>
                      <Table.Td>
                        <ActionIcon
                          component='a'
                          href={(() => {
                            const params = new URLSearchParams();
                            if (jobFamilyId) params.append('jobfamilyid', jobFamilyId);
                            if (managementLevel) params.append('managementlevel', managementLevel);
                            if (businessUnitId) params.append('businessunitid', businessUnitId);
                            if (jobFamilyName) params.append('jobfamilyname', jobFamilyName);
                            if (businessUnitName)
                              params.append('businessunitname', businessUnitName);
                            if (competencyType) params.append('competencytype', competencyType);

                            // Add job positions if available
                            if (
                              (item as any).jobPositions &&
                              Array.isArray((item as any).jobPositions)
                            ) {
                              const positions = (item as any).jobPositions.map((pos: any) => ({
                                id: typeof pos === 'object' && pos?.id ? pos.id : '',
                                name: typeof pos === 'object' && pos?.name ? pos.name : String(pos)
                              }));
                              params.append('lpdJobPositions', JSON.stringify(positions));
                            }

                            return `/app/dacum/management-competency/update/${item.competencyId}?${params.toString()}`;
                          })()}
                          target='_blank'
                          variant='light'
                          color='blue'
                          size='sm'
                          aria-label='Edit competency'
                        >
                          <IconEdit size={16} />
                        </ActionIcon>
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        )}

        <Group justify='center'>
          <Pagination total={Math.max(totalPages, 1)} value={page} onChange={setPage} />
        </Group>

        <Group justify='flex-end'>
          <Button variant='default' onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={Object.keys(selection).length === 0}>
            Add Curriculum Items
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

interface CurriculumCourseSelectionModalProps {
  opened: boolean;
  onClose: () => void;
  onSubmit: (_courseId: string) => void;
  curriculumContext?: {
    competencyName: string;
    category: string;
    level: number;
    competencyType: string;
  } | null;
}

const CurriculumCourseSelectionModal: React.FC<CurriculumCourseSelectionModalProps> = ({
  opened,
  onClose,
  onSubmit,
  curriculumContext
}) => {
  const [searchKeyword, setSearchKeyword] = React.useState('');
  const [page, setPage] = React.useState(1);
  const [selectedCourseId, setSelectedCourseId] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!opened) {
      setSearchKeyword('');
      setPage(1);
      setSelectedCourseId(null);
    }
  }, [opened]);

  const { data, isFetching } = useQuery({
    queryKey: ['curriculum-course-selector', searchKeyword, page],
    queryFn: () =>
      courseService.list({
        search: searchKeyword,
        pageNumber: page,
        pageSize: DEFAULT_PAGE_SIZE
      }),
    placeholderData: (previousData) => previousData,
    enabled: opened
  });

  const courses: CourseOption[] = (data?.data || []).map((item: any) => ({
    msLCMSCourseID: item.msLCMSCourseID || item.courseId || item.id,
    courseName: item.courseName || item.name || '-',
    courseCode: item.courseCode || item.code,
    courseCategory: item.courseCategory || item.category,
    status: item.status,
    competencyCategory: item.competencyCategory
  }));
  const totalPages = data?.totalPages || 1;

  const handleConfirm = () => {
    if (selectedCourseId) {
      onSubmit(selectedCourseId);
      setSelectedCourseId(null);
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} centered size='70%' radius='lg' shadow='lg'>
      <Stack gap='lg'>
        <Stack gap={4}>
          <Text fw={700} size='lg'>
            Select Course for Curriculum
          </Text>
          <Text size='sm' c='dimmed'>
            Choose a course to assign to this curriculum item. Course details will be fetched
            automatically.
          </Text>
        </Stack>

        {curriculumContext && (
          <Alert color='blue' radius='md' variant='light'>
            <Stack gap={4}>
              <Text fw={600} size='sm'>
                Updating course for:
              </Text>
              <Group gap='xs'>
                <Badge color='blue' variant='light' size='lg'>
                  {curriculumContext.competencyName}
                </Badge>
                <Badge color='gray' variant='outline' size='md'>
                  {curriculumContext.category}
                </Badge>
                <Badge color='gray' variant='outline' size='md'>
                  Level {curriculumContext.level}
                </Badge>
                <Badge color='grape' variant='light' size='md'>
                  {curriculumContext.competencyType}
                </Badge>
              </Group>
            </Stack>
          </Alert>
        )}

        <TextInput
          placeholder='Search courses by name, code, or category'
          value={searchKeyword}
          onChange={(event) => {
            setSearchKeyword(event.currentTarget.value);
            setPage(1);
          }}
          leftSection={<IconSearch size={16} stroke={1.6} />}
          radius='md'
          size='md'
          variant='filled'
        />

        {isFetching ? (
          <Flex justify='center' align='center' h={120}>
            <Loader size='sm' />
          </Flex>
        ) : courses.length === 0 ? (
          <Text size='sm' c='dimmed'>
            No courses found.
          </Text>
        ) : (
          <ScrollArea h={400} offsetScrollbars>
            <Table striped highlightOnHover withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th style={{ width: 70 }}>Select</Table.Th>
                  <Table.Th>Course Name</Table.Th>
                  <Table.Th>Course Code</Table.Th>
                  <Table.Th>Category</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Competency Category</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {courses.map((course) => (
                  <Table.Tr
                    key={course.msLCMSCourseID}
                    onClick={() => setSelectedCourseId(course.msLCMSCourseID)}
                    style={{
                      cursor: 'pointer',
                      backgroundColor:
                        selectedCourseId === course.msLCMSCourseID
                          ? 'var(--mantine-color-blue-0)'
                          : undefined
                    }}
                  >
                    <Table.Td>
                      <Checkbox
                        checked={selectedCourseId === course.msLCMSCourseID}
                        onChange={() => setSelectedCourseId(course.msLCMSCourseID)}
                        aria-label={`Select ${course.courseName}`}
                      />
                    </Table.Td>
                    <Table.Td>
                      <Stack gap={2} align='flex-start'>
                        <Text fw={600} size='md'>
                          {course.courseName}
                        </Text>
                        <Text size='xs' c='dimmed'>
                          {course.msLCMSCourseID}
                        </Text>
                      </Stack>
                    </Table.Td>
                    <Table.Td>{course.courseCode || '-'}</Table.Td>
                    <Table.Td>{course.courseCategory || '-'}</Table.Td>
                    <Table.Td>{course.status || '-'}</Table.Td>
                    <Table.Td>{course.competencyCategory || '-'}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        )}

        <Group justify='center'>
          <Pagination total={Math.max(totalPages, 1)} value={page} onChange={setPage} />
        </Group>

        <Group justify='flex-end'>
          <Button variant='default' onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={!selectedCourseId}>
            Update Course
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default CreateMasterPlanForm;
